import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';
import { GlobalNotificationProvider } from './contexts/GlobalNotificationContext';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

// Create Material-UI theme with mobile-responsive overrides
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
      light: '#ff5983',
      dark: '#9a0036',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    success: {
      main: '#2e7d32',
    },
    warning: {
      main: '#ed6c02',
    },
    error: {
      main: '#d32f2f',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 500,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Prevent horizontal scrolling on the entire page
        html: {
          overflowX: 'hidden',
          maxWidth: '100vw',
        },
        body: {
          overflowX: 'hidden',
          maxWidth: '100vw',
        },
        // Prevent iOS zoom on input focus (font-size must be >= 16px)
        '@media (max-width: 768px)': {
          'input, select, textarea': {
            fontSize: '16px !important',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 8,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 6,
          minHeight: 44, // Touch-friendly minimum height
          '@media (max-width: 768px)': {
            minHeight: 44,
            fontSize: '0.875rem',
          },
        },
        sizeSmall: {
          minHeight: 36,
          '@media (max-width: 768px)': {
            minHeight: 40,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '@media (max-width: 768px)': {
            minWidth: 44,
            minHeight: 44,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          '@media (max-width: 768px)': {
            padding: '8px 12px',
            fontSize: '0.8125rem',
          },
        },
        head: {
          '@media (max-width: 768px)': {
            whiteSpace: 'nowrap',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '@media (max-width: 768px)': {
            minHeight: 44,
            minWidth: 'auto',
            padding: '8px 12px',
            fontSize: '0.8125rem',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '@media (max-width: 768px)': {
            minHeight: 44,
          },
        },
        scrollButtons: {
          '&.Mui-disabled': {
            opacity: 0.3,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          '@media (max-width: 768px)': {
            margin: 16,
            width: 'calc(100% - 32px)',
            maxWidth: 'calc(100% - 32px)',
            maxHeight: 'calc(100% - 32px)',
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          '@media (max-width: 768px)': {
            padding: '16px',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: 44, // Touch-friendly nav items
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '@media (max-width: 768px)': {
            fontSize: '16px', // Prevent iOS zoom
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '@media (max-width: 768px)': {
            height: 28,
            fontSize: '0.75rem',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          '@media (max-width: 768px)': {
            fontSize: '0.8125rem',
            padding: '6px 12px',
          },
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalNotificationProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </GlobalNotificationProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
